import type { DialogParams, DialogPayload } from 'types/dialog';
import { create } from 'zustand';

type DialogState = {
  /** 연 순서대로. 마지막이 맨 위다. */
  dialogs: DialogPayload[];
  /** 확인이면 true, 그 밖의 닫힘은 false 로 끝난다. */
  open: (params: DialogParams) => Promise<boolean>;
  close: (id: string, confirmed: boolean) => void;
  dismiss: (id: string) => void;
  expire: (id: string) => void;
  closeAll: () => void;
};

// resolve 는 그릴 값이 아니라 상태 밖에 둔다.
const resolves = new Map<string, (confirmed: boolean) => void>();
let seq = 0;
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const clearTimer = (id: string) => {
  clearTimeout(timers.get(id));
  timers.delete(id);
};

const notify = (callback?: () => void) => {
  try {
    callback?.();
  } catch (error) {
    console.error('Dialog callback failed', error);
  }
};

const useDialogStore = create<DialogState>((set, get) => ({
  dialogs: [],
  open: (params) =>
    new Promise((resolve) => {
      const id = params.id ?? `dialog-${++seq}`;
      const previous = resolves.get(id);
      previous?.(false);
      clearTimer(id);
      resolves.set(id, resolve);
      set((state) => ({ dialogs: [...state.dialogs.filter((dialog) => dialog.id !== id), { ...params, id }] }));
      if (params.autoCloseMs !== undefined) {
        timers.set(
          id,
          setTimeout(() => get().expire(id), params.autoCloseMs)
        );
      }
    }),
  close: (id, confirmed) => {
    // 닫히는 애니메이션 도중 한 번 더 눌러도 결과는 한 번만 나간다.
    const resolve = resolves.get(id);
    if (!resolve) return;
    resolves.delete(id);
    clearTimer(id);
    const closing = get().dialogs.find((dialog) => dialog.id === id);
    // 콜백이 같은 ID의 새 팝업을 열어도 새 요청은 제거하지 않는다.
    if (confirmed) notify(closing?.onConfirm);
    set((state) => ({ dialogs: state.dialogs.filter((dialog) => dialog !== closing) }));
    resolve(confirmed);
  },
  dismiss: (id) => {
    const dialog = get().dialogs.find((item) => item.id === id);
    if (!dialog) return;
    const callback = dialog.onCancel;
    get().close(id, false);
    notify(callback);
  },
  expire: (id) => {
    const dialog = get().dialogs.find((item) => item.id === id);
    if (!dialog) return;
    const callback = dialog.onTimeout;
    get().close(id, false);
    notify(callback);
  },
  closeAll: () => {
    const pending = Array.from(resolves.values());
    resolves.clear();
    timers.forEach((_, id) => clearTimer(id));
    set({ dialogs: [] });
    pending.forEach((resolve) => resolve(false));
  },
}));

export default useDialogStore;
